import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import {
  FaBuilding, FaChartBar, FaShieldAlt, FaSignOutAlt, FaBars, FaTimes,
  FaCreditCard, FaTag, FaUserSecret, FaUsers,
} from 'react-icons/fa';
import BrandLogo from '../components/common/BrandLogo';
import { MdDashboard, MdLeaderboard, MdLink } from 'react-icons/md';
import saApi from '../services/superAdminApi';
import useSuperAuth from '../hooks/useSuperAuth';
import ForcePasswordChange from '../pages/superadmin/ForcePasswordChange';
import { applyThemeToDom, getStoredTheme } from '../context/ThemeContext';

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { path: '/superadmin/dashboard', label: 'Dashboard', Icon: MdDashboard },
    ],
  },
  {
    label: 'Business',
    items: [
      { path: '/superadmin/clubs',         label: 'Clubs',         Icon: FaBuilding      },
      { path: '/superadmin/subscriptions', label: 'Subscriptions', Icon: FaCreditCard    },
      { path: '/superadmin/discounts',     label: 'Discounts',     Icon: FaTag           },
      { path: '/superadmin/demo-links',    label: 'Demo Links',    Icon: MdLink          },
      { path: '/superadmin/leads',         label: 'Sales Leads',   Icon: MdLeaderboard   },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: '/superadmin/impersonation', label: 'Impersonation', Icon: FaUserSecret },
      { path: '/superadmin/audit-logs',    label: 'Audit Logs',    Icon: FaShieldAlt  },
      { path: '/superadmin/team',          label: 'Team',          Icon: FaUsers      },
    ],
  },
];

// Flat list kept for header label lookup
const ALL_NAV = NAV_SECTIONS.flatMap(s => s.items);

export default function SuperAdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { superAdmin, saToken, loading, logout, loginSA } = useSuperAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !saToken) navigate('/superadmin', { replace: true });
  }, [loading, saToken, navigate]);

  // Superadmin pages are ALWAYS dark and ignore the club theme preference. Force the dark
  // class on mount (overriding whatever a club light/dark toggle left on the document root),
  // and on unmount restore the club's saved preference. We only override the applied class —
  // the shared localStorage theme key is never written from superadmin.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('dark');    body.classList.add('dark');
    root.classList.remove('light'); body.classList.remove('light');
    root.dataset.theme = 'dark';   body.dataset.theme = 'dark';
    return () => { applyThemeToDom(getStoredTheme()); };
  }, []);

  const { data: countData } = useQuery({
    queryKey: ['sa-new-leads-count'],
    queryFn:  () => saApi.get('/api/super-admin/leads/new-count').then(r => r.data),
    enabled:  !!saToken,
    refetchInterval: 60 * 1000, // refresh every minute
    staleTime: 30 * 1000,
  });
  const newLeadsCount = countData?.count || 0;

  const { data: pendingPayData } = useQuery({
    queryKey: ['sa-pending-payments-count'],
    queryFn:  () => saApi.get('/api/subscription/admin/platform-payments/pending-count').then(r => r.data),
    enabled:  !!saToken,
    refetchInterval: 60 * 1000, // refresh every minute
    staleTime: 30 * 1000,
  });
  const pendingPayCount = pendingPayData?.count || 0;

  if (!saToken) return null;

  // Forced first-login password change (newly-added team members) — gate the whole
  // panel until the temp password is replaced. Survives reloads (flag is persisted).
  if (superAdmin?.mustChangePassword) {
    return (
      <ForcePasswordChange
        superAdmin={superAdmin}
        onDone={() => loginSA({ ...superAdmin, mustChangePassword: false }, saToken)}
        onSignOut={logout}
      />
    );
  }

  const currentLabel = ALL_NAV.find(n => location.pathname.startsWith(n.path))?.label || 'Super Admin';

  return (
    <div className="min-h-screen flex" style={{ background: '#141312', color: '#ede8e0' }}>
      {/* ── Sidebar ── */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-56 flex flex-col flex-shrink-0 transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0`}
        style={{ background: '#1a1918', borderRight: '1px solid rgba(237,232,224,0.06)' }}>

        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'rgba(237,232,224,0.06)' }}>
          <div className="flex items-center gap-2.5">
            <BrandLogo size={30} />
            <div>
              <div className="font-bold text-[#c8102e] text-sm tracking-wide">TrainAxis</div>
              <div className="text-xs mt-0.5 font-semibold" style={{ color: '#c8102e' }}>Platform Owner</div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-[#8a847c] hover:text-[#ede8e0]">
            <FaTimes />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-3">
          {NAV_SECTIONS.map(section => (
            <div key={section.label} className="mb-4">
              <div className="px-3 mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] select-none"
                style={{ color: '#6b6560' }}>
                {section.label}
              </div>
              <div className="space-y-0.5">
                {section.items.map(({ path, label, Icon }) => {
                  const active     = location.pathname.startsWith(path);
                  const isLeads    = path === '/superadmin/leads';
                  const isSubs     = path === '/superadmin/subscriptions';
                  const badgeCount = isLeads ? newLeadsCount : isSubs ? pendingPayCount : 0;
                  const showBadge  = badgeCount > 0;
                  return (
                    <Link key={path} to={path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                        active ? 'bg-[#c8102e] text-white' : 'text-[#8a847c] hover:bg-[#28261f] hover:text-[#ede8e0]'
                      }`}
                      style={active ? { background: '#c8102e', color: '#fff' } : {}}>
                      <Icon className="flex-shrink-0 text-base" />
                      <span className="flex-1 min-w-0 truncate">{label}</span>
                      {showBadge && (
                        <span style={{
                          background: active ? 'rgba(255,255,255,0.25)' : '#c8102e',
                          color: '#fff',
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '1px 5px',
                          minWidth: 18,
                          textAlign: 'center',
                          lineHeight: '16px',
                          flexShrink: 0,
                        }}>
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User profile + logout */}
        <div className="p-4 border-t flex-shrink-0" style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
              style={{ background: '#c8102e', color: '#fff' }}>
              {superAdmin?.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#ede8e0] truncate">{superAdmin?.name || 'Admin'}</div>
              <div className="text-xs truncate" style={{ color: '#6b6560' }}>{superAdmin?.email}</div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold capitalize px-2 py-0.5 rounded-full"
              style={{ background: '#c8102e', color: '#fff' }}>
              {superAdmin?.role || 'support'}
            </span>
            <button onClick={logout}
              className="flex items-center gap-1.5 text-xs transition-colors"
              style={{ color: '#6b6560' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#e01535')}
              onMouseLeave={e => (e.currentTarget.style.color = '#6b6560')}>
              <FaSignOutAlt /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="flex items-center gap-3 px-5 py-3 border-b flex-shrink-0"
          style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.06)' }}>
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[#8a847c] hover:text-[#ede8e0]">
            <FaBars className="text-lg" />
          </button>
          <span className="text-sm font-semibold text-[#ede8e0]">{currentLabel}</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded-full font-bold" style={{ background: '#c8102e', color: '#fff' }}>
              SUPER ADMIN
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6" style={{ background: '#141312' }}>
          <Outlet />
        </main>
        <Toaster position="top-right" toastOptions={{ duration: 3500, style: { fontSize: '0.875rem', background: '#1e1d1b', color: '#ede8e0', border: '1px solid rgba(237,232,224,0.08)' } }} />
      </div>
    </div>
  );
}
